// Strength catalogue. Each entry maps to a unique products.strength value
// in the DB and drives the /strength/:slug pages: rendering, SSR meta,
// and sitemap.

export type StrengthBand = "light" | "medium" | "strong" | "extra-strong";

export interface StrengthInfo {
  /** URL slug — "6mg", "16-5mg", etc. Decimals use hyphens for URL safety. */
  slug: string;
  /** Numeric value matching products.strength */
  value: number;
  /** Display label e.g. "6mg" */
  label: string;
  /** Category band for filter UI + copy */
  band: StrengthBand;
  description: string;
  seoTitle: string;
  seoDescription: string;
}

function valueToSlug(value: number): string {
  return `${String(value).replace(".", "-")}mg`;
}

export function slugToStrengthValue(slug: string): number | null {
  const m = /^(\d+(?:-\d+)?)mg$/i.exec(slug);
  if (!m) return null;
  const num = Number.parseFloat(m[1]!.replace("-", "."));
  return Number.isFinite(num) ? num : null;
}

function classify(value: number): StrengthBand {
  if (value <= 6) return "light";
  if (value <= 12) return "medium";
  if (value <= 17) return "strong";
  return "extra-strong";
}

function buildDescription(value: number, band: StrengthBand): string {
  const label = `${value}mg`;
  switch (band) {
    case "light":
      return `${label} nicotine pouches are the perfect entry point for first-time users, light nicotine users, or anyone stepping down from a stronger habit. Mild, manageable and easy to wear at any time of day without overwhelming. In India, this strength is most common in VELO Mini, ZYN Apple Mint and CLEW pouches — clean, smooth, and gentle on the gums.`;
    case "medium":
      return `${label} nicotine pouches deliver a well-balanced hit — strong enough to satisfy regular users, gentle enough to wear comfortably across the day. This is the most popular strength range in India, covering most ZYN Cool Mint variants, VELO 10mg pouches, and the standard ZYN Slim range. A solid starting point if you've already tried a lighter pouch.`;
    case "strong":
      return `${label} nicotine pouches are made for experienced users who want a serious hit. This range includes White Fox Slim, VELO Freezing Peppermint, ZYN Cool Mint Max, and the lighter end of the Pablo line — bold flavours, intense nicotine release, and 30–45 minute sessions. Not recommended for first-time users.`;
    case "extra-strong":
      return `${label} nicotine pouches are among the strongest available worldwide, designed exclusively for very experienced users. The Pablo Exclusive line and a handful of Pablo standard variants reach these levels. The nicotine hit is significant and the session shorter — typically 15–25 minutes. Do not start here if you're new to pouches.`;
  }
}

function buildSeoTitle(label: string, band: StrengthBand): string {
  switch (band) {
    case "light":
      return `${label} Nicotine Pouches in India | Light & Beginner-Friendly | ZeroSmoke`;
    case "medium":
      return `${label} Nicotine Pouches in India | ZYN, VELO, CLEW | ZeroSmoke`;
    case "strong":
      return `${label} Nicotine Pouches in India | Strong & Bold | ZeroSmoke`;
    case "extra-strong":
      return `${label} Nicotine Pouches in India | Extra-Strong Pablo & More | ZeroSmoke`;
  }
}

function buildSeoDescription(label: string, band: StrengthBand): string {
  switch (band) {
    case "light":
      return `Shop ${label} nicotine pouches in India — mild, beginner-friendly strength from ZYN, VELO, CLEW. Tobacco-free, smoke-free, delivered nationwide.`;
    case "medium":
      return `Buy ${label} nicotine pouches online in India. Balanced everyday strength from ZYN, VELO, CLEW. Tobacco-free, authentic, fast delivery.`;
    case "strong":
      return `Shop ${label} strong nicotine pouches in India — White Fox, VELO, ZYN and Pablo variants. Tobacco-free, bold, fast delivery nationwide.`;
    case "extra-strong":
      return `Buy ${label} extra-strong nicotine pouches in India — Pablo Exclusive and more. Tobacco-free, for experienced users only. Authentic, fast shipping.`;
  }
}

function entry(value: number): StrengthInfo {
  const label = `${value}mg`;
  const band = classify(value);
  return {
    slug: valueToSlug(value),
    value,
    label,
    band,
    description: buildDescription(value, band),
    seoTitle: buildSeoTitle(label, band),
    seoDescription: buildSeoDescription(label, band),
  };
}

// All unique strengths in the catalogue. If a new product introduces a
// strength that's not here, add it to this list — the resolver, sitemap,
// and StorePage automatically pick up new entries.
export const strengths: StrengthInfo[] = [
  3, 4, 6, 8, 9, 10, 11, 12, 13, 14, 16.5, 17, 20, 30, 50,
].map(entry);

export const strengthBySlug: Record<string, StrengthInfo> = Object.fromEntries(
  strengths.map((s) => [s.slug, s])
);
